import React, { useState, useContext, useEffect } from 'react';
import { CheckCircleOutline, NotInterested, Edit, LockOutlined, InfoOutlined } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import TableLoadingOverlay from './components/TableLoadingOverlay';
import DataTable from '../../dataTable';
import { IDataTableColumn } from '../../dataTable/DataTable/model';
import { IContainer } from '../model';
import FormCreateEditContainer from './components/FormCreateEditContainer';
import FormConfirmDelete from './components/FormConfirmDelete';
import { ContainersContext, ContainersProvider } from './contexts';
import { updateContainer, getContainerById } from './services';
import { convertLengthValue } from '../utils';

// Create a separate component for the container content
const ContainersContent: React.FunctionComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const location = useLocation();
  const { containerSerial } = useParams<{ containerSerial?: string }>();

  const { containers, loading, refreshContainers } = useContext(ContainersContext);
  const [includeInactive, setIncludeInactive] = useState<boolean>(true);
  const [handleCreateEditForm, setHandleCreateEditForm] = useState<Partial<IContainer>>();
  const [handleDeleteForm, setHandleDeleteForm] = useState<IContainer>();
  const [loadingContainer, setLoadingContainer] = useState<boolean>(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState<boolean>(false);

  // Apply filters when checkbox state changes
  useEffect(() => {
    refreshContainers(includeInactive);
  }, [includeInactive]);

  // Load containers only once on component mount
  useEffect(() => {
    if (!initialDataLoaded) {
      refreshContainers(includeInactive);
      setInitialDataLoaded(true);
    }
  }, [initialDataLoaded]);

  // Load container data for edit operation based on URL parameters
  useEffect(() => {
    const handleRouteChange = async () => {
      if (location.state && typeof location.state === 'object' && 'hasData' in location.state) {
        if ((location.state as { hasData: boolean }).hasData) return;
      }

      // Check if we're on the create route
      if (location.pathname.includes('/edgelink/containers/create')) {
        setHandleCreateEditForm({});
        return;
      }

      // Check if we're on the edit route with a valid container ID
      if (containerSerial && location.pathname.includes('/edit/')) {
        const containerId = parseInt(containerSerial, 10);
        if (!Number.isNaN(containerId)) {
          setLoadingContainer(true);
          try {
            const container = await getContainerById(containerId);
            if (container) {
              setHandleCreateEditForm(container);
            } else {
              // Invalid container ID, redirect to containers list
              history.push('/edgelink/containers');
            }
          } catch (error) {
            console.error('Error loading container:', error);
            history.push('/edgelink/containers');
          } finally {
            setLoadingContainer(false);
          }
        }
      } else {
        // Reset form state when not on create or edit routes
        setHandleCreateEditForm(undefined);
      }
    };

    handleRouteChange();
  }, [location.pathname, containerSerial, history]);

  const statusIconStyle: React.CSSProperties = {
    borderRadius: '50%',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
  };

  // Handle container status change (activate/deactivate)
  const handleStatusChange = async (container: IContainer, newStatus: boolean) => {
    try {
      await updateContainer(container.tank_config_id, { status: newStatus });
      await refreshContainers(includeInactive);
    } catch (error) {
      console.error('Error changing container status:', error);
    }
  };

  // Navigate to edit page without refreshing data
  const handleEditContainer = (container: IContainer) => {
    setHandleCreateEditForm(container);
    history.push(`/edgelink/containers/edit/${container.tank_config_id}`, { hasData: true });
  };

  // Navigate to create page without refreshing data
  const handleCreateContainer = () => {
    setHandleCreateEditForm({});
    history.push('/edgelink/containers/create');
  };

  // Close form and redirect back to containers list
  const handleCloseForm = () => {
    setHandleCreateEditForm(undefined);
    history.push('/edgelink/containers');
  };

  const columns: IDataTableColumn<IContainer>[] = [
    {
      name: 'Status',
      selector: (row) => row.status,
      cell: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', minWidth: isMobile ? '80px' : 'auto' }}>
          {row.status ? (
            <Tooltip title="Active">
              <CheckCircleOutline
                style={{
                  color: '#4caf50',
                  ...statusIconStyle,
                  fontSize: isMobile ? '1.4rem' : '1.5rem',
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Inactive">
              <NotInterested
                style={{
                  color: '#f44336',
                  ...statusIconStyle,
                  fontSize: isMobile ? '1.4rem' : '1.5rem',
                }}
              />
            </Tooltip>
          )}
          <span
            style={{
              marginLeft: isMobile ? '4px' : '8px',
              whiteSpace: 'nowrap',
            }}
          >
            {row.status ? 'Active' : 'Inactive'}
          </span>
        </div>
      ),
      sortable: true,
      maxWidth: '7em',
    },
    {
      name: 'Tank Name',
      selector: (row) => row.tank_name,
      sortable: true,
      cell: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', width: '100%' }}>
          {row.is_system_default ? (
            <Tooltip
              title="System default cannot be edited"
              arrow
              placement={isMobile ? 'top' : 'right'}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.grey[800],
                    color: 'white',
                    fontSize: isMobile ? '1rem' : '1.3rem',
                    fontWeight: 'medium',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    boxShadow: theme.shadows[2],
                  },
                },
              }}
            >
              <LockOutlined
                style={{
                  fontSize: isMobile ? '1.8rem' : '2.5rem',
                  padding: '4px',
                  color: theme.palette.text.disabled,
                }}
              />
            </Tooltip>
          ) : (
            <Edit
              className="edit-indicator"
              style={{
                fontSize: isMobile ? '1.8rem' : '2.5rem',
                visibility: 'hidden',
                padding: '4px',
                color: theme.palette.primary.main,
              }}
            />
          )}
          <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isMobile ? 'normal' : 'nowrap' }}>
              {row.tank_name}
            </div>
            {row.is_system_default && <em style={{ display: 'block' }}>(System Default)</em>}
          </div>
        </div>
      ),
    },
    {
      name: 'Dimensions',
      selector: (row) => '',
      cell: (row) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            fontSize: isMobile ? '1.05rem' : 'inherit',
            padding: '8px 0',
            lineHeight: '1.3',
          }}
        >
          {row.dimensions.map((dimension, index) => {
            // Convert dimension value from millimeters (API value) to the display unit
            let displayValue = dimension.value;
            if (displayValue && row.distance_uom) {
              // Convert from millimeters to the unit specified in the container
              displayValue = convertLengthValue(displayValue, 'millimeters', row.distance_uom.name);
              // Round to 2 decimal places for display
              displayValue = Number(displayValue.toFixed(2));
            }
            return (
              <div
                key={index}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: dimension.name === 'Sensor Offset' ? 'normal' : 'inherit',
                }}
              >
                <strong style={{ fontWeight: 600 }}>{dimension.name}:</strong>{' '}
                {displayValue ? `${displayValue} ${row.distance_uom.abbreviation}` : 'N/A'}
              </div>
            );
          })}
        </div>
      ),
      sortable: false,
    },
    {
      name: 'Created By',
      selector: (row) => row.created_by,
      sortable: true,
      maxWidth: '10em',
    },
    {
      name: 'Created At',
      selector: (row) => new Date(row.created_at).toLocaleDateString(),
      sortable: true,
      maxWidth: '10em',
    },
    {
      name: 'Updated By',
      selector: (row) => row.updated_by || 'N/A',
      sortable: true,
      maxWidth: '10em',
    },
    {
      name: 'Updated At',
      selector: (row) => (row.updated_at ? new Date(row.updated_at).toLocaleDateString() : 'N/A'),
      sortable: true,
      maxWidth: '10em',
    },
    // Actions column removed as rows are now clickable
  ];

  // Show loading when fetching a specific container for editing
  if (loadingContainer) {
    return <CircularProgress />;
  }

  // Adjust columns based on screen size
  const responsiveColumns = isMobile
    ? columns.filter(
        (col) =>
          // Keep only essential columns for mobile view
          col.name === 'Status' || col.name === 'Tank Name' || col.name === 'Dimensions',
      )
    : columns;

  return (
    <>
      <FormCreateEditContainer container={handleCreateEditForm} onClose={() => handleCloseForm()} />
      <FormConfirmDelete
        container={handleDeleteForm}
        onConfirm={async () => {
          await refreshContainers(includeInactive);
          setHandleDeleteForm(undefined);
        }}
        onCancel={() => setHandleDeleteForm(undefined)}
      />
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'stretch' : 'center'}
        spacing={isMobile ? 1 : 2}
        mb={isMobile ? 1 : 2}
      >
        <Box>
          <Typography variant="h1" component="h1" m={0} fontSize={isMobile ? '1.5rem' : 'inherit'}>
            Containers
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              mt: 1,
              mb: 1,
              color: theme.palette.text.secondary,
              flexWrap: 'wrap',
              fontSize: isMobile ? '0.8rem' : 'inherit',
            }}
          >
            <InfoOutlined fontSize="small" sx={{ mr: 1, mt: '2px' }} />
            <Typography variant="body2" fontSize="inherit">
              Click on a row to edit a container. System default containers (with lock icon) cannot be edited.
            </Typography>
          </Box>
        </Box>

        <Stack
          direction={isMobile ? 'column' : 'row'}
          alignItems="center"
          spacing={isMobile ? 1 : 2}
          width={isMobile ? '100%' : 'auto'}
          mt={isMobile ? 1 : 0}
        >
          {/* <FormControlLabel
            control={<Checkbox checked={includeInactive} onChange={() => setIncludeInactive(!includeInactive)} />}
            label="Include Inactive"
          /> */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateContainer}
            fullWidth={isMobile}
            sx={{ py: isMobile ? 1 : 'inherit' }}
          >
            Create
          </Button>
        </Stack>
      </Stack>
      <Box position="relative" sx={{ overflowX: 'auto' }}>
        {loading && <TableLoadingOverlay />}
        <DataTable
          columns={responsiveColumns}
          data={containers}
          noHeader
          striped
          highlightOnHover
          pointerOnHover
          dense={isMobile}
          onRowClicked={(row) => !row.is_system_default && handleEditContainer(row)}
          conditionalRowStyles={[
            {
              when: (row) => row.is_system_default,
              style: {
                backgroundColor: theme.palette.action.hover,
                opacity: 0.8,
                cursor: 'default',
              },
            },
            {
              when: (row) => !row.is_system_default,
              style: {
                '&:hover': {
                  cursor: 'pointer',
                  '& .edit-indicator': {
                    visibility: 'visible',
                  },
                },
              },
            },
          ]}
        />
      </Box>
    </>
  );
};

// Main container component that wraps the content with the provider
const ContainersPage: React.FunctionComponent = () => {
  return (
    <ContainersProvider>
      <ContainersContent />
    </ContainersProvider>
  );
};

export default ContainersPage;
